import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { trackActivityThunk } from 'actions/gamificationActions';
import { markSearchTracked, clearSearchTracked } from 'store/reducers/gamification.reducer';
import { getHasTrackedSearch } from 'store/selectors/gamification.selector';
import { getIsGamificationEnabled } from 'store/selectors/auth.selector';

interface ILenderMetadata {
  type: 'route_one_id' | 'dealertrack_id';
  id: string;
}

interface IUseSearchTrackingOptions {
  /** Increments each time the search form is reset; triggers a new trackable cycle. */
  resetCount: number;
  /**
   * Optional: when a new, truthy conversation ID arrives the tracking window
   * resets (Lender-specific).  Pass `undefined` to skip this check.
   */
  conversationId?: string | null;
  /**
   * Optional: when provided the hook auto-tracks a search whenever
   * `isFetchingVehicles` transitions from `true` to `false` without an error
   * (ProbabilityEstimator / RAC pattern).  Pass `undefined` to skip.
   */
  isFetchingVehicles?: boolean;
  isFetchingVehiclesFailed?: boolean;
  /** Lender metadata included in tracked activity. */
  lender?: ILenderMetadata | null;
  /** Marks the tracked activity as RAC (the RAC flow has no per-deal lender id). */
  isRac?: boolean;
}

interface IUseSearchTrackingResult {
  /** Marks the current search as tracked and dispatches the activity. Idempotent within a tracking cycle. */
  trackSearch: () => void;
}

export function useSearchTracking({
  resetCount,
  conversationId,
  isFetchingVehicles,
  isFetchingVehiclesFailed,
  lender,
  isRac = false,
}: IUseSearchTrackingOptions): IUseSearchTrackingResult {
  const { pathname: source } = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const gamificationEnabled = useSelector(getIsGamificationEnabled);
  const hasTrackedSearch = useSelector((state: RootState) => getHasTrackedSearch(state, source));

  const lenderType = lender?.type;
  const lenderId = lender?.id;

  const buildMetadata = useCallback((): Record<string, unknown> => {
    const metadata: Record<string, unknown> = {};

    if (lenderType && lenderId) metadata.lender = { type: lenderType, id: lenderId };

    if (isRac) metadata.is_rac = true;

    return metadata;
  }, [lenderType, lenderId, isRac]);

  const hasTrackedSearchRef = useRef(hasTrackedSearch);
  const wasFetchingRef = useRef(false);
  const prevResetCountRef = useRef(resetCount);
  const prevConversationIdRef = useRef(conversationId);

  useEffect(() => {
    hasTrackedSearchRef.current = hasTrackedSearch;
  }, [hasTrackedSearch]);

  useEffect(() => {
    if (resetCount !== prevResetCountRef.current) {
      prevResetCountRef.current = resetCount;

      if (hasTrackedSearchRef.current) {
        hasTrackedSearchRef.current = false;
        dispatch(clearSearchTracked(source));
      }
    }
  }, [resetCount, dispatch, source]);

  useEffect(() => {
    if (conversationId === undefined) return;

    if (conversationId && conversationId !== prevConversationIdRef.current) {
      prevConversationIdRef.current = conversationId;

      if (hasTrackedSearchRef.current) {
        hasTrackedSearchRef.current = false;
        dispatch(clearSearchTracked(source));
      }
    }
  }, [conversationId, dispatch, source]);

  useEffect(() => {
    if (isFetchingVehicles === undefined) return;

    if (wasFetchingRef.current && !isFetchingVehicles && !isFetchingVehiclesFailed) {
      if (gamificationEnabled && !hasTrackedSearchRef.current) {
        hasTrackedSearchRef.current = true;
        dispatch(markSearchTracked(source));
        dispatch(trackActivityThunk({ activity_type: 'search', metadata: buildMetadata() }));
      }
    }

    wasFetchingRef.current = isFetchingVehicles;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keep visibility fixed for this fetch cycle
  }, [isFetchingVehicles, isFetchingVehiclesFailed, dispatch, source, buildMetadata]);

  const trackSearch = useCallback(() => {
    if (gamificationEnabled && !hasTrackedSearchRef.current) {
      hasTrackedSearchRef.current = true;
      dispatch(markSearchTracked(source));
      dispatch(trackActivityThunk({ activity_type: 'search', metadata: buildMetadata() }));
    }
  }, [dispatch, source, gamificationEnabled, buildMetadata]);

  return { trackSearch };
}
